
import Shifty from '@deepsource/shifty'
import { defineComponent, PropType } from 'vue'
import ActiveUserMixin from '~/mixins/activeUserMixin'
import { FeatureType } from '~/types/features'
import { ReportMeta, ReportPageT } from '~/types/reportTypes'
import {
  CreatePublicReportInput,
  ReportLevel,
  ReportSource,
  ReportType,
  UpdateAction,
  UpdatePublicReportInput,
  UpdatePublicReportSourcedRepositoriesInput
} from '~/types/types'
import { canAccessFeature } from '~/utils/billing'

const reportPageToFeature: Partial<Record<ReportPageT, FeatureType>> = {
  [ReportPageT.OWASP_TOP_10]: FeatureType.OWASP_TOP_10,
  [ReportPageT.SANS_TOP_25]: FeatureType.SANS_TOP_25
}

/**
 * ? Why create and update in same modal
 *
 * On an individual report page, we have a share button. On clicking it, this modal opens with
 * the reportKeys prefilled, which is done through reprtoKeys being prop controlled.
 * Since we'll anyways need to support prop controlled reportKeys, we added support for
 * all data properties to be prop controlled. Hence this modal works for both, report creation and updation.
 */

/**
 * Create Public Report modal
 */
export default defineComponent({
  name: 'MutateOwnerReportModal',
  mixins: [ActiveUserMixin],
  props: {
    level: {
      type: String as PropType<ReportLevel>,
      required: true
    },
    editMode: {
      type: Boolean,
      required: true
    },
    ownerLogin: {
      type: String,
      required: true
    },
    vcsProvider: {
      type: String,
      required: true
    },
    reportIdOld: {
      type: String,
      default: ''
    },
    isRestrictedOld: {
      type: Boolean,
      default: false
    },
    reportKeysOld: {
      type: Array as PropType<Array<ReportPageT>>,
      default: () => []
    },
    shareHistoricalDataOld: {
      type: Boolean,
      default: false
    },
    reportLabelOld: {
      type: String,
      default: ''
    },
    reportSourceOld: {
      type: String as PropType<ReportSource>,
      default: ReportSource.SourceAll
    },
    sourceRepoCountOld: {
      type: Number,
      default: 0
    },
    saveLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isRestricted: false,
      password: '',
      reportKeys: [] as Array<ReportPageT>,
      shareHistoricalData: false,
      reportLabel: '',
      reportSource: ReportSource.SourceAll,
      sourceRepoCount: 0,
      repoListToAdd: [] as Array<string>,
      repoListToRemove: [] as Array<string>,
      isPasswordHidden: false,
      showPasswordInput: false,
      showRepoSelection: false,
      engine: new Shifty(false, 16),
      ReportMeta,
      ReportType,
      ReportSource
    }
  },
  emits: ['create-report', 'edit-report', 'close'],
  watch: {
    '$route.path': {
      handler() {
        this.closeModal()
      }
    }
  },
  mounted() {
    this.reportKeys = this.reportKeysOld.filter((key) => !this.disabledReportKeys.has(key))
    this.shareHistoricalData = this.shareHistoricalDataOld
    this.reportLabel = this.reportLabelOld
    this.isRestricted = this.isRestrictedOld
    this.reportSource = this.reportSourceOld

    if (this.reportSourceOld !== ReportSource.SourceAll) {
      this.sourceRepoCount = this.sourceRepoCountOld
    }
  },
  computed: {
    reportUrl(): string {
      return `https://${this.$config.domain}/report/${this.reportIdOld}`
    },
    showCreateButton(): boolean {
      return this.reportSource === ReportSource.SourceAll ? true : this.showRepoSelection
    },
    disabledReportKeys(): Set<ReportPageT> {
      const disabled = new Set<ReportPageT>()
      if (this.$config.onPrem) return disabled
      for (const [reportKey, featureType] of Object.entries(reportPageToFeature)) {
        if (!canAccessFeature(featureType, this.activeDashboardContext?.subscribed_plan_info)) {
          disabled.add(reportKey as ReportPageT)
        }
      }
      return disabled
    }
  },
  methods: {
    /**
     * Method to handle password-protected toggle updation and, based on value received from it,
     * control visibility of password input field .
     *
     * @param passworProtected boolean
     *
     * @return void
     */
    handlePasswordProtectedToggle(passworProtected: boolean) {
      /**
       * We receive new value of password-protected toggle as argument.
       * If this new value is true, that means we need to show the password input field,
       * for which we need to also generate a new password.
       *
       * However, we want to show the password input field only if the report was not
       * password-protected already, because in that case we wan't to show the password
       * reset button.
       */
      if (passworProtected) {
        this.generatePassword()
        if (!this.isRestrictedOld) {
          this.showPasswordInput = true
        }
      } else {
        this.password = ''
        this.showPasswordInput = false
      }
    },
    /**
     * Click handler method for password reset button.
     * Generates new password and makes password input field visible.
     *
     * @return void
     */
    handleResetPassword() {
      this.generatePassword()
      this.showPasswordInput = true
    },

    /**
     * handle repo selection event
     *
     * @param {Array<string>} addedRepos
     * @returns void
     */
    handleRepoAddition(addedRepos: Array<string>): void {
      this.sourceRepoCount += 1

      // Only add unique repo IDs
      this.repoListToAdd = Array.from(new Set([...this.repoListToAdd, ...addedRepos]))

      // If a repo is  deselected and reselected again, it'll be present in repoListToRemove
      // Hence, we need to remove it from repoListToRemove since it's selected again.
      this.repoListToRemove = this.repoListToRemove.filter((repoId) => !addedRepos.includes(repoId))
    },

    /**
     * handle repo unselection event
     *
     * @param {Array<string>} removedRepos
     * @returns void
     */
    handleRepoRemoval(removedRepos: Array<string>): void {
      this.sourceRepoCount -= 1

      // Only add unique repo IDs
      this.repoListToRemove = Array.from(new Set([...this.repoListToRemove, ...removedRepos]))

      // If a repo is  selected and then deselected again, it'll be present in repoListToAdd
      // Hence, we need to remove it from repoListToAdd since it's deselected agaion.
      this.repoListToAdd = this.repoListToAdd.filter((repoId) => !removedRepos.includes(repoId))
    },

    /**
     * Click handler button for save report button.
     *
     * @param close {() => void}
     *
     * @return void
     */
    handleSave(close?: () => void) {
      if (this.editMode) {
        this.emitEditReport(close)
      } else {
        this.emitCreateReport(close)
      }
    },

    /**
     * Method to build args for report creation and emit it.
     *
     * @oaram close {() => void}
     *
     * @return void
     */
    emitCreateReport(close?: () => void) {
      if (this.validateArgs()) {
        const newReportArgs: CreatePublicReportInput = {
          ownerLogin: this.ownerLogin,
          vcsProvider: this.$providerMetaMap[this.vcsProvider].value,
          label: this.reportLabel,
          level: this.level,
          isRestricted: this.isRestricted,
          password: this.password,
          shareHistoricalData: this.shareHistoricalData,
          reportKeys: this.reportKeys,
          source: this.reportSource,
          sourcedRepositories: this.repoListToAdd
        }

        if (Object.keys(newReportArgs).length) {
          this.$emit('create-report', newReportArgs, close)
        }
      }
    },

    /**
     * Callback function to close modal.
     *
     * @function {Close}
     * @returns {void}
     */

    /**
     * Method to build args for report updation and emit it.
     *
     * @param {Close} close
     *
     * @return void
     */
    emitEditReport(close?: () => void) {
      if (this.validateArgs()) {
        const updatedReportArgs: UpdatePublicReportInput = {
          reportId: this.reportIdOld,
          label: this.reportLabel,
          shareHistoricalData: this.shareHistoricalData,
          reportKeys: this.reportKeys,
          isRestricted: this.isRestricted
        }

        if (this.isRestricted && this.password.length) {
          updatedReportArgs['password'] = this.password
        }

        if (this.reportSourceOld !== this.reportSource) {
          updatedReportArgs['source'] = this.reportSource
        }

        const addRepoArgs: UpdatePublicReportSourcedRepositoriesInput = {
          reportId: this.reportIdOld,
          action: UpdateAction.Add,
          repositoryIds: this.reportSource === ReportSource.SourceSelected ? this.repoListToAdd : []
        }

        const removeRepoArgs: UpdatePublicReportSourcedRepositoriesInput = {
          reportId: this.reportIdOld,
          action: UpdateAction.Remove,
          repositoryIds:
            this.reportSource === ReportSource.SourceSelected ? this.repoListToRemove : []
        }

        // In case report source is being turnt to SourceSelected, sourcedRepositories field is required
        // Moreover, in such a case, the user would be choosing repos for the first time for the respective report,
        // so there are no reports to be removed per se.
        if (
          this.reportSourceOld === ReportSource.SourceAll &&
          this.reportSource === ReportSource.SourceSelected
        ) {
          updatedReportArgs['sourcedRepositories'] = this.repoListToAdd
          addRepoArgs['repositoryIds'] = []
        }

        if (Object.keys(updatedReportArgs).length) {
          this.$emit('edit-report', updatedReportArgs, addRepoArgs, removeRepoArgs, close)
        }
      }
    },

    /**
     * Generate password using shifty
     *
     * @return {void}
     */
    generatePassword(): void {
      if (!this.engine) this.engine = new Shifty(false, 16)

      this.password = this.engine.generate()
      if (this.isPasswordHidden) {
        this.isPasswordHidden = false
        setTimeout(() => {
          this.isPasswordHidden = true
        }, 1500)
      }
    },

    /**
     * Toggle password visibility
     *
     * @return {void}
     */
    togglePasswordVisibility(): void {
      this.isPasswordHidden = !this.isPasswordHidden
    },

    /**
     * Validate arguments for create/edit report mutation
     *
     * @return boolean
     */
    validateArgs(): boolean {
      // Check for password only if password input is shown
      if (this.showPasswordInput && !this.password) {
        this.$toast.danger("Please enter a password or disable 'Restrict report access'.")
        return false
      }

      if (this.showPasswordInput && this.password && this.password.length < 8) {
        this.$toast.danger('Password must be at least 8 characters long.')
        return false
      }

      if (!this.reportLabel) {
        this.$toast.danger('Please add a label for the report.')
        return false
      }

      if (this.reportKeys.length < 1) {
        this.$toast.danger('Please select at least one report to share.')
        return false
      }

      if (this.reportSource === ReportSource.SourceSelected && this.sourceRepoCount < 1) {
        this.$toast.danger('Please select at least one repository, or select all repositories.')
        return false
      }

      return true
    },
    closeModal(): void {
      this.$emit('close')
    }
  }
})
